import { message } from 'antd'
import _ from 'lodash'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import dayjs from 'dayjs'
import {
  BET_SLIP_TAB_INDEX_KEY,
  BET_TICKET_TYPE,
  COOKIE_KEYS,
  DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE,
  GIFT_BTN_TYPE,
  HASH_PARAMETER,
  HOME_OR_AWAY_SCORE,
  HOMEPAGE_SERVER_TYPE,
  ITEM_IMG_EXT,
  JOIN_URL,
  LOCALSTORAGE_KEYS,
  MESSAGE_SECOND_DURATION,
  PUBLISH_PICK_TYPE,
  RATIO_TICKET,
} from '@constants/index'
import { Schema$MatchupMarket } from '@protocol/game'
import { CartItem, DetailMatchTrack, MatchTrack, OutcomeTrack, SimpleMatchTrack } from '@store/matchupStore'
import { AuthStore } from '@store/authStore'
import { EventStatusType } from '@game-type/betradar-type'
import {
  ACCOUNT_TYPE,
  LS_SPORTS_TYPE_NUM,
  PAGE_TYPE,
  PAGE_WITHOUT_HEADER,
  Schema$UserMission,
  SPORTS_TYPE,
  SPORTS_TYPE_NUM,
} from '@interface/common'
import { MissionType } from '@game-type/mission-type'
import {
  AUTHENTICATION_TYPE,
  BET_TYPE_ON,
  CLUB_MEMBERSHIP_ID,
  LS_SPORT_ID,
  MATCH_STATUS,
  PROPERTY_ID,
  SPORT_ID,
} from 'adv-protocol/src/GameCommon'
import { ArrDayText } from '@game/util'
import dateFormat from 'dateformat'
import { Schema$ComponentBettingParamFold, Schema$ComponentBettingParamMatch } from '@protocol/component'
import { BettingRecordTrack } from '@store/bettingHistoryStore'
import { Schema$PickMatchInfo } from '@protocol/pick'
import { authStore, globalStore, matchupStore, routerStore, userStore } from '@store/index'
import {
  CHARACTER_CHECK_EXPRESSION,
  COMMUNICATION_BLOCK_TYPE,
  GAME_KIND,
  GlobalStoreKey,
  LS_FIXTURE_STATUS,
  MARKET_PROVIDER,
  PASSWORD_KEYBOARD_SEQUENCE_WORDS,
} from '@v2/constants/common'
import DummyImg from '@assets/images/itemshop/skin_dummy.png'
import { ComponentMatchTeamName } from '@store/componentStore'
import {
  GAME_TYPE,
  LS_MAJOR_SPORT_IDS,
  MAJOR_SPORT_IDS,
  TIMELINE_POPUP_ENABLED_SPORT_IDS,
} from '@game-type/game-market-types'
import StorageUtils from '@utils/storage-utils'
import { SimpleMatchTimelineGroup } from '@interface/chat'
import { USER_ACTIVITY_TYPE } from '@v2/constants/activity'

export function convertRate(isSigned: boolean, rate: number, isHiddenRate: boolean = false): string {
  if (isHiddenRate) {
    return ''
  }

  return rate && rate > 0 ? rate.toFixed(2) : ''
}

export function formatNumber(src: number | string | undefined) {
  const value = String(src || 0)
  const list = value.split('.')
  const prefix = list[0].charAt(0) === '-' ? '-' : ''
  let num = prefix ? list[0].slice(1) : list[0]
  let result = ''
  while (num.length > 3) {
    result = `,${num.slice(-3)}${result}`
    num = num.slice(0, num.length - 3)
  }
  if (num) {
    result = num + result
  }
  return `${prefix}${result}${list[1] ? `.${list[1]}` : ''}`
}

export function formatSimpleNumber(src: number | string | undefined) {
  return formatNumber(src)
}

const timeout = ms => new Promise(res => setTimeout(res, ms))

export async function delay(ms) {
  await timeout(ms)
}

export enum TOAST_TYPE {
  SUCCESS = 'success',
  INFO = 'info',
  WARN = 'warn',
  WARNING = 'warning',
  ERROR = 'error',
}

let messageDestroyTimerId

export const showToast = (type: TOAST_TYPE = TOAST_TYPE.INFO, content: React.ReactNode | string) => {
  if (type === TOAST_TYPE.SUCCESS) {
    message.success(content)
  } else if (type === TOAST_TYPE.INFO) {
    message.info(content)
  } else if (type === TOAST_TYPE.WARN) {
    message.warn(content)
  } else if (type === TOAST_TYPE.WARNING) {
    message.warning(content)
  } else if (type === TOAST_TYPE.ERROR) {
    message.error(content)
  }

  if (messageDestroyTimerId) {
    clearTimeout(messageDestroyTimerId)
  }

  messageDestroyTimerId = setTimeout(() => {
    messageDestroyTimerId = null
    message.destroy()
  }, MESSAGE_SECOND_DURATION * 1000 + 500)
}

export function findString(value: string, searchValue: string) {
  if (value.indexOf(searchValue) !== -1) {
    return `<BettorName>${value}</BettorName>`
  }
  return `${value}`
}

/** 세트 스코어를 표시해야 하는 종목 (배구, LS배구, 탁구, 테니스) */
export function isSetScoreSport(sportId: number): boolean {
  return (
    sportId === SPORTS_TYPE_NUM.VOLLEYBALL
    || sportId === LS_SPORTS_TYPE_NUM.VOLLEYBALL
    || sportId === SPORTS_TYPE_NUM.TABLE_TENNIS
    || sportId === SPORTS_TYPE_NUM.TENNIS
  )
}

export function getHomeOrAwayLiveScoreWithPeriodScores(
  homeOrAway: string, match: MatchTrack | SimpleMatchTrack, sportId: number, isLive: boolean,
): string {
  const { periodScores, awayScore, homeScore, statusCode } = match

  // NOTE : 라이브 경기가 시작 및 진행중인 상태에서 스코어 정보가 없을경우 0으로 세팅
  let score = statusCode === EventStatusType.Live ? '0' : '-'
  if (isLsportsEnv()) {
    score = statusCode === LS_FIXTURE_STATUS.IN_PROGRESS ? '0' : '-'
  }
  if (homeOrAway === HOME_OR_AWAY_SCORE.HOME) {
    score = homeScore ? homeScore.toString() : score
  } else {
    score = awayScore ? awayScore.toString() : score
  }

  // NOTE: 모든 종목의 periodScores type은 regular_period로 들어온다.
  // 확인 결과 homeScore, awayScore는 각 periodScores의 home or away 점수의 합산된 값이다.
  // 배구의 경우 합산된 스코어를 뿌려주는 방식이 아니기 때문에 `(마지막세트스코어)최종 스코어` 식으로 뿌려준다
  if (isSetScoreSport(sportId) && isLive) {
    let periodScore = '-'

    if (periodScores?.length > 0) {
      const lastPeriodScore = periodScores[periodScores.length - 1]
      const matchStatusCode = (match as MatchTrack).matchStatusCode
      // 세트가 바뀐 경우: lastPeriodScore의 matchStatus가 현재 matchStatusCode와 다르면
      // 새 세트가 시작된 것이므로 현재 세트 내 점수는 0으로 표시
      const isSetChanged = matchStatusCode != null && lastPeriodScore.matchStatus !== matchStatusCode
      if (isSetChanged) {
        periodScore = '0'
      } else if (homeOrAway === HOME_OR_AWAY_SCORE.HOME) {
        periodScore = `${lastPeriodScore.homeScore}`
      } else {
        periodScore = `${lastPeriodScore.awayScore}`
      }
    }

    return `${periodScore} ${score}`
  }
  return score
}

export function getHomeOrAwayScore(
  homeOrAway: string, match: MatchTrack
): number | string {
  return match[homeOrAway]
}

export function titleTextPatternCheck(text: string): boolean {
  return /^[가-힣a-zA-Z\d\s]+$/.test(text)
}

export class Profiler {
  date: Date

  name: string

  begin = (n: string) => {
    this.name = n
    this.date = new Date()
  }

  end = () => {
    const tmp = new Date()
    const offset = tmp.valueOf() - this.date.valueOf()
    console.error(`profiler: ${this.name} offset= ${offset} millisec`)
  }
}

export const PlayingPeriodSpecifierKey = [
  'quarternr',
  'inningnr',
  'setnr',
  'gamenr',
  'mapnr',
  'goalnr',
  'framenr',
  'periodnr',
  'pointnr',
  'runnr',
]

export function makeEmblemPath(prefix: string, path_prefix: string, small: string, big: string) {
  if (small) {
    return `${prefix}${path_prefix}_s/${small}`
  }
  if (big) {
    return `${prefix}${path_prefix}_b/${big}`
  }
  return ''
}

export function filterPrematch(matches: Schema$MatchupMarket[], subKey: string) {
  let filteredMatches = []
  const etcFilter: number[] = (isLsportsEnv() ? MAJOR_SPORT_IDS : LS_MAJOR_SPORT_IDS)
  switch (Number(subKey)) {
    case 0:
      filteredMatches = filteredMatches.concat(matches)
      break
    case 1: // 축구 1
      filteredMatches = filteredMatches.concat(matches.filter((item: Schema$MatchupMarket) => {
        return item.sportId === SPORT_ID.SOCCER || item.sportId === LS_SPORT_ID.FOOTBALL
      }))
      break
    case 2: // 농구 2
      filteredMatches = filteredMatches.concat(matches.filter((item: Schema$MatchupMarket) => {
        return item.sportId === SPORT_ID.BASKETBALL || item.sportId === LS_SPORT_ID.BASKETBALL
      }))
      break
    case 3: // 야구 3
      filteredMatches = filteredMatches.concat(matches.filter((item: Schema$MatchupMarket) => {
        return item.sportId === SPORT_ID.BASEBALL || item.sportId === LS_SPORT_ID.BASEBALL
      }))
      break
    case 4: // 배구 23
      filteredMatches = filteredMatches.concat(matches.filter((item: Schema$MatchupMarket) => {
        return item.sportId === SPORT_ID.VOLLEYBALL || item.sportId === LS_SPORT_ID.VOLLEYBALL
      }))
      break
    case 5: // 아이스하키 4
      filteredMatches = filteredMatches.concat(matches.filter((item: Schema$MatchupMarket) => {
        return item.sportId === SPORT_ID.ICE_HOCKEY || item.sportId === LS_SPORT_ID.ICE_HOCKEY
      }))
      break
    case 6: // 기타
      filteredMatches = filteredMatches.concat(
        matches.filter((item: Schema$MatchupMarket) => etcFilter.indexOf(item.sportId) === -1),
      )
      break
  }
  return filteredMatches
}

export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function setCookie(name: string, value: string, expireDate) {
  document.cookie = `${name}=${escape(value)};expires=${expireDate.toUTCString()}`
}

export function getCookie(name: string) {
  if (document.cookie.length > 0) {
    let cStart = document.cookie.indexOf(`${name}=`)
    if (cStart !== -1) {
      cStart = cStart + name.length + 1
      let cEnd = document.cookie.indexOf(';', cStart)
      if (cEnd === -1) {
        cEnd = document.cookie.length
      }
      return unescape(document.cookie.substring(cStart, cEnd))
    }
  }
  return ''
}

export function clearCookie(name: string) {
  setCookie(name, '', new Date())
}

export function makeCookieKeyByUser(cookieKeyName: string, _authStore: AuthStore) {
  return `${cookieKeyName}_${_authStore.accountInfo.userNum}`
}

export function isTutorialVisibleFromCookie() {
  const cookieValue = getCookie(COOKIE_KEYS.TUTORIAL_DISPLAYED_BY_CIVALUE)
  return cookieValue === ''
}

export function makeDetailMessage(detailMessageSource: string) {
  let detailMessage = detailMessageSource
  let detailMessageObj: { mobile: string, pc: string }
  try {
    detailMessageObj = JSON.parse(detailMessage)
  } catch (e) {
    console.error(e)
  }

  if (_.isObject(detailMessageObj)) {
    if (isMobile && detailMessageObj.mobile) {
      detailMessage = detailMessageObj.mobile
    } else if (detailMessageObj.pc) {
      detailMessage = detailMessageObj.pc
    }
  }
  return detailMessage
}

enum INPUT_KEYTYPE {
  ENTER = 13,
  SPACE_BAR = 32,
}

export function isKeyInputEnterAndSpaceBar(keyCode: number) {
  if (keyCode === INPUT_KEYTYPE.ENTER ||
    keyCode === INPUT_KEYTYPE.SPACE_BAR) {
    return true
  }

  return false
}

export function isProdEnv() {
  return _.toLower(process.env.APP_ENV) === 'prod' || _.toLower(process.env.APP_ENV) === 'dev-pr'
}

export function isQaEnv() {
  return _.toLower(process.env.APP_ENV) === 'qa'
    || _.toLower(process.env.APP_ENV) === 'local-qa'
    || _.toLower(process.env.APP_ENV) === 'dev-qa'
}

export function isTestEnv() {
  return _.toLower(process.env.APP_ENV) === 'test'
    || _.toLower(process.env.APP_ENV) === 'newTest'
    || _.toLower(process.env.APP_ENV) === 'dev-ts'
}

export function isMissionCompleted(mission: Schema$UserMission): boolean {
  // 배당률의 경우 소숫점으로 기록이 되는데 currentCount 필드는 정수만 취급하여 기록시 100을 곱해서 기록되어 있기 때문
  const currentCount = mission.conditionType === MissionType.TOP_HIT_RATE
    ? Number(mission.currentCount) / 100 : mission.currentCount
  return Number(currentCount) >= Number(mission.clearCount)
}

export function isSpecialMarket(marketType: string) {
  if (_.isNaN(Number(marketType))) {
    return false
  }
  return Number(marketType) >= 30000
}

export function getTicketCountFromTicket(type: BET_TICKET_TYPE, ticket: number) {
  if (ticket === 0) {
    return 0
  }

  // NOTE: 소수점 버림
  return Math.floor(ticket / type)
}

export function formatNumberTicketCount(type: BET_TICKET_TYPE, ticket: number) {
  return formatNumber(getTicketCountFromTicket(type, ticket))
}

export function getTicketFromTicketCount(type: BET_TICKET_TYPE, ticket: number) {
  return type * ticket
}

export function getPickPublishMoney(type: PUBLISH_PICK_TYPE, money: number) {
  return type === PUBLISH_PICK_TYPE.HEART ? money : getTicketCountFromTicket(BET_TICKET_TYPE.BET_TICKET1, money)
}

export function tooltipTicketCount(type: BET_TICKET_TYPE, ticket: number) {
  const ratio_ticket = 10 // 10만 기준으로 베팅권 보이면서 만 단위로 표현하기위해 10으로 나눠준다
  const ticketCount = getTicketCountFromTicket(type, ticket)
  return Math.floor((ticket - (ticketCount * type)) / ratio_ticket)
}

// NOTE: 실제로는 10만베팅권이지만 1만 베팅권으로 표현해주기위한 func
export function getRatioTicketMoney(money: number) {
  return money / RATIO_TICKET
}

export function getBetTypeName(betType: BET_TYPE_ON) {
  switch (betType) {
    case BET_TYPE_ON.UNIFIED_MATCH:
      return '스포츠'
    case BET_TYPE_ON.UNIFIED_LIVE:
      return '라이브'
    case BET_TYPE_ON.UNIFIED_SPECIAL:
      return '스페셜'
  }

  return ''
}

export function getGiftTypeName(type: GIFT_BTN_TYPE) {
  switch (type) {
    case GIFT_BTN_TYPE.HEART:
      return '하트'
    case GIFT_BTN_TYPE.TICKET:
      return '베팅권'
    default:
      return '하트'
  }
}

export function existExtraInningInfoVisibleSportId(sportId: number) {
  const sportIds = globalStore.getExtraInningInfoVisibleSportIds()
  return sportIds.find(id => _.toNumber(id) === _.toNumber(sportId))
}

export function noExistExtraInningInfoVisibleSportId(sportId: number) {
  const sportIds = globalStore.getNoExtraInningInfoVisibleSportIds()
  return sportIds.find(id => _.toNumber(id) === _.toNumber(sportId))
}

export function getHashParameter(key) {
  let parameter = ''

  const hash = window.location.hash
  if (key && hash.indexOf(key) > -1) {
    const parameterStartIndex = hash.indexOf('?') + 1
    const textParameters = hash.substr(parameterStartIndex)
    const splitTextParameters = textParameters.split('&')
    if (splitTextParameters) {
      parameter = splitTextParameters.find(splitTextParameter => splitTextParameter.indexOf(key) > -1)
    }
  }

  return parameter
}

export function getHashParameterValue(key) {
  let value = ''

  const parameter = getHashParameter(key)
  if (parameter) {
    const splitParameter = parameter.split('=')
    if (splitParameter.length === 2) {
      value = splitParameter[1]
    }
  }

  return value
}

export function getAttachableHashParameters(withQuestionPrefix) {
  let parameter = ''
  const keys = Object.keys(HASH_PARAMETER)
  const lastKey = keys[keys.length - 1]
  for (const key of keys) {
    if (!HASH_PARAMETER[key].ATTACHABLE) {
      continue
    }
    if (!_.isEmpty(parameter) && lastKey !== key && !_.endsWith(parameter, '&')) {
      parameter += '&'
    }
    const hash = getHashParameter(HASH_PARAMETER[key].KEY)
    parameter += hash
  }

  const prefix = withQuestionPrefix ? '?' : '&'
  return parameter ? `${prefix}${parameter}` : ''
}

export function getAllHashParameters(withQuestionPrefix) {
  let parameter = ''
  const keys = Object.keys(HASH_PARAMETER)
  const lastKey = keys[keys.length - 1]
  for (const key of keys) {
    if (!_.isEmpty(parameter) && lastKey !== key && !_.endsWith(parameter, '&')) {
      parameter += '&'
    }
    const hash = getHashParameter(HASH_PARAMETER[key].KEY)
    parameter += hash
  }

  const prefix = withQuestionPrefix ? '?' : '&'
  return parameter ? `${prefix}${parameter}` : ''
}

export function getBettingTime(createdAt: string) {
  const dateInst = new Date(createdAt)
  const dateText = dateFormat(dateInst, 'mm/dd')
  const timeText = dateFormat(
    dateInst,
    'HH:MM',
  ) /* 디자인 시안상 초단위는 없으며 있을 경우 공간 부족으로 주석처리해놓았습니다. */
  // const timeText = dateFormat(dateInst, 'HH:MM:ss')
  const monthText = ArrDayText[new Date(dateInst).getDay()]
  const dateOfBet = `${dateText}(${monthText}) ${timeText}`
  return dateOfBet
}

export function getDayOfWeeks(date: string) {
  const days = ['일', '월', '화', '수', '목', '금', '토']
  if (!_.isEmpty(date)) {
    const dayOfWeek = dayjs(date).day()
    return _.nth(days, dayOfWeek)
  }
  return ''
}

export function schema$PickMatchInfoToSchema$ComponentBettingParamMatches(
  pickMatches: Schema$PickMatchInfo[],
): Schema$ComponentBettingParamMatch[] {
  const matches: Schema$ComponentBettingParamMatch[] = []

  pickMatches.forEach(match => {
    const folds: Schema$ComponentBettingParamFold[] = []
    const matchId = match.matchId
    match.folds.forEach(fold => {
      folds.push({ ...fold, matchId })
    })
    matches.push({ ...match, folds })
  })
  return matches
}

export function bettingRecordTrackToSchema$ComponentBettingParamMatch(
  track: BettingRecordTrack,
): Schema$ComponentBettingParamMatch[] {
  const matches: Schema$ComponentBettingParamMatch[] = []
  const foldGroupKeys = Object.keys(track.folds)
  const isSingleBet = _.size(track.folds) === 1

  for (const foldGroupKey of foldGroupKeys) {
    const foldGroups = track.folds[foldGroupKey]
    const folds: Schema$ComponentBettingParamFold[] = []
    foldGroups.data.forEach(fold => {
      const selection = fold.selection
      const outcome = fold.oddsFields.find(oddsField => oddsField.type === selection)

      if (isSingleBet) {
        outcome.rate = _.toNumber(fold.outcomeRate)
      }

      folds.push({
        matchId: foldGroups.matchId,
        marketType: fold.marketType,
        specifiers: JSON.parse(fold.specifiers),
        outcome,
      })
    })

    matches.push({
      matchId: folds[0].matchId,
      homeTeamName: foldGroups.homeTeamName,
      awayTeamName: foldGroups.awayTeamName,
      folds,
    })
  }

  return matches
}

export function isEventPage(): boolean {
  let result = false
  if (window.location.href.indexOf('m-eventPage') > -1) {
    result = true
  }
  return result
}

export function isAdvCasinoJoinPage(): boolean {
  let result = false
  if (_.toLower(window.location.hash).indexOf(JOIN_URL.AGREE) > -1
    && getHashParameterValue(HASH_PARAMETER.GAME_KIND.KEY) === _.toString(GAME_KIND.CASINO)) {
    result = true
  }
  return result
}

export function isNaverAuthResultPage(): boolean {
  let result = false
  if (window.location.href.indexOf('naverAuthResult') > -1) {
    result = true
  }
  return result
}

// 참고: https://zorba91.tistory.com/266
function getDecimal(rate: number, percent: number) {
  return +(rate * percent).toFixed(12)
}

export function getSingleBetPenaltyRate(dividendRate: number, userId: string): number {
  const defaultPenalty = globalStore.findGlobalSetting(GlobalStoreKey.BETTING_PENALTY_SINGLE)?.value || '0'
  const userPenaltyListString = globalStore.findGlobalSetting(GlobalStoreKey.BETTING_PENALTY_SINGLE_USER_LIST)?.value
  let userPenaltyValue = defaultPenalty
  try {
    const userPenaltyList = JSON.parse(userPenaltyListString)
    const userPenalty = _.find(userPenaltyList, { userId })
    if (userPenalty) {
      userPenaltyValue = userPenalty.penalty
    }
  } catch (err) {
    console.error(err)
  }
  const result = Math.floor(getDecimal(dividendRate, 100) - getDecimal(dividendRate, Number(userPenaltyValue))) * 0.01
  return result
}

export const checkLength = (minLength: number, maxLength: number, value = ''): boolean => {
  return !(value.length < minLength || value.length > maxLength)
}

export const checkNonTripleDuplicatedCharacter = (value = ''): boolean => {
  return !(value.search(/(.)\1\1+/gi) > -1)
}

/** 순서가 연속된 문자열 검출하는 함수
 * 문자의 Unicode 값을 이용하여 비교
 * 예) abc, 123, xyz 등
 * @param value 비밀번호 문자열
 */
export const checkSequenceCharacter = (value = ''): boolean => {
  const limit = 2
  let count = 0
  let prevCharCode = 0

  for (let i = 0; i < value.length; i += 1) {
    const charCode = value.charCodeAt(i)

    if (prevCharCode > 0) {
      if (prevCharCode + 1 === charCode) {
        count += 1
      } else {
        count = 0
      }
    }

    if (count >= limit) {
      break
    }

    prevCharCode = charCode
  }

  return count < limit
}

export const checkPasswordContainsKeyboardSequenceWords = (value = ''): boolean => {
  const loweredPassword = _.toLower(value)
  let result = true

  for (const word of PASSWORD_KEYBOARD_SEQUENCE_WORDS) {
    if (_.includes(loweredPassword, word)) {
      result = false
      break
    }
  }

  return result
}

export const checkOnlyNumberWithEnglish = (value = ''): boolean => {
  const pattern = /^[a-zA-Z\d]+$/
  return existNumber(value) && existEnglish(value) && pattern.test(value)
}

export const checkPasswordCharacterCombination = (value: string): boolean => {
  let existCount = 0

  if (existNumber(value)) {
    existCount += 1
  }

  if (existEnglishLowerCase(value)) {
    existCount += 1
  }

  if (existEnglishUpperCase(value)) {
    existCount += 1
  }

  if (existSpecial(value)) {
    existCount += 1
  }

  return existCount > 1
}

export const checkOnlyOfNumberAndEnglishAndSpecial = (value = ''): boolean => {
  const pattern = /^[a-zA-Z~!@#$%^&*|\\'';:/?\d]+$/

  return pattern.test(value)
}

export const checkOnlyNumberAndEnglishAndKorean = (value = ''): boolean => {
  const pattern = /^[가-힣a-zA-Z\d]+$/
  return pattern.test(value)
}

export const checkEmail = (value = ''): boolean => {
  const pattern = new RegExp(/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i)
  return pattern.test(value)
}

export const checkUrl = (value: string): boolean => {
  const pattern = new RegExp(/(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/i)
  return pattern.test(value)
}

/**
 * Google Play 스토어 링크(https://play.google.com/store/~)인지 판별한다.
 */
export const isGooglePlayStoreUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.host === 'play.google.com' && _.startsWith(parsedUrl.pathname, '/store/')
  } catch (e) {
    return false
  }
}

export const checkNonWhiteSpace = (value = ''): boolean => {
  return _.replace(value, ' ', '').length === value.length
}

export const checkStartOfEnglish = (value = ''): boolean => {
  const pattern = new RegExp(/^[a-z]/, 'i')
  return pattern.test(value)
}

export const existNumber = (value = ''): boolean => {
  return value.search(CHARACTER_CHECK_EXPRESSION.NUMBER) > -1
}

export const existEnglish = (value = ''): boolean => {
  return value.search(CHARACTER_CHECK_EXPRESSION.ENGLISH) > -1
}

export const existEnglishUpperCase = (value = ''): boolean => {
  return value.search(CHARACTER_CHECK_EXPRESSION.ENGLISH_UPPER_CASE) > -1
}

export const existEnglishLowerCase = (value = ''): boolean => {
  return value.search(CHARACTER_CHECK_EXPRESSION.ENGLISH_LOWER_CASE) > -1
}

export const existSpecial = (value = ''): boolean => {
  return value.search(CHARACTER_CHECK_EXPRESSION.SPECIAL) > -1
}

export const existWhiteSpace = (value = ''): boolean => {
  return _.replace(value, ' ', '').length !== value.length
}

export const isNotSupportedBrowser = (): boolean => {
  // 모바일이 아닌 경우 모든 브라우저 지원
  if (!isMobile) {
    return false
  }

  // PWA 실행 모드가 standalone 이므로 해당 모드가 아닌 경우 모든 브라우저 지원
  if (!window.matchMedia('(display-mode: standalone)').matches) {
    return false
  }

  // PWA가 삼성 브라우저로 실행되지 않은 경우 모든 브라우저 지원
  const userAgents = _.toLower(navigator.userAgent).split(' ')
  const samsungBrowserText = _.toLower('SamsungBrowser')
  const samsungBrowserInfo = _.find(userAgents, userAgent => userAgent.indexOf(samsungBrowserText) > -1)
  if (_.isEmpty(samsungBrowserInfo)) {
    return false
  }

  // 삼성브라우저 중 20버전 이상은 지원
  const infos = samsungBrowserInfo.split('/')
  const version = _.toNumber(infos[1])
  return version < 20
}

export const isIosNaverInAppBrowser = (): boolean => {
  if (!isMobile || isAndroid) {
    return false
  }

  const agent = navigator.userAgent.toLowerCase()
  return /NAVER/i.test(agent)
}

export const isAndroidInAppBrowser = (): boolean => {
  if (!isMobile || !isAndroid) {
    return false
  }

  const isTelegramWebview = !!window.TelegramWebview

  const agent = navigator.userAgent.toLowerCase()
  const isKakaoTalk = /kakaotalk/i.test(agent)

  return isTelegramWebview || isKakaoTalk || isAndroidNaverInAppBrowser()
}

export const isAndroidNaverInAppBrowser = (): boolean => {
  if (!isMobile) {
    return false
  }

  const agent = navigator.userAgent.toLowerCase()
  const isWebview = /wv/i.test(agent)
  const isNaver = /NAVER/i.test(agent)

  return isWebview && isNaver
}

export function maxTicketBetMoney() {
  const clubGmoneyMaxBetLimit = globalStore.getClubGmoneyMaxBetLimit()
  return clubGmoneyMaxBetLimit * 10
}

export function isCustomProfileImg(profileImg: string) {
  return _.includes(profileImg, 'user_profile')
}

export function makeItemImageUrl(item): string {
  if (item && item.icon) {
    const dataUrl = globalStore.findGlobalSetting(GlobalStoreKey.URL_DATACENTER)
    if (dataUrl) {
      const imageUrl = `${dataUrl.value}item_image`
      return `${imageUrl}/${item.icon}${ITEM_IMG_EXT}`
    }
  }
  return DummyImg
}

export const isSocialAccountType = (type: ACCOUNT_TYPE) => {
  return type !== ACCOUNT_TYPE.LOCAL && type !== ACCOUNT_TYPE.AUTH_KEY
}

// 코드를 더 안전하게 변경해야 한다.
export const getNaverAccessToken = () => {
  let accessToken

  const hash = window.location.hash
  if (hash && hash.lastIndexOf('#') > -1) {
    let parameter = ''

    const parameterStartIndex = hash.lastIndexOf('#') + 1
    const textParameters = hash.substr(parameterStartIndex)
    const splitTextParameters = textParameters.split('&')
    if (splitTextParameters) {
      parameter = splitTextParameters.find(splitTextParameter => splitTextParameter.indexOf('access_token') > -1)
      const keyAndValue = parameter.split('=')
      if (keyAndValue && keyAndValue.length === 2) {
        accessToken = keyAndValue[1]
      }
    }
  }

  return accessToken
}

export function makeComponentMatchTeamName(homeTeamName: string, awayTeamName: string): ComponentMatchTeamName {
  const matchTeamName: ComponentMatchTeamName = {
    homeTeamName,
    awayTeamName,
  }

  return matchTeamName
}

export function makeSelectedOutcome(type: string, rate: number, name: string, metaId: string): OutcomeTrack {
  const selectedOutcome: OutcomeTrack = {
    type,
    rate,
    name,
    metaId,
  }

  return selectedOutcome
}

/**
 * 베팅 슬립 탭 키에 따른 재화 id 를 반환한다.
 * 베팅 슬립 탭 마다 대상 베팅 재화가 달라진다.
 *
 * @param key
 */
export function getPropertyIdByBetSlipIndexKey(key: BET_SLIP_TAB_INDEX_KEY): PROPERTY_ID {
  let id: PROPERTY_ID
  switch (key) {
    case BET_SLIP_TAB_INDEX_KEY.MONEY:
      id = PROPERTY_ID.MONEY
      break
    case BET_SLIP_TAB_INDEX_KEY.BET_TICKET:
      id = PROPERTY_ID.NEW_BET_TICKET_G
      break
    case BET_SLIP_TAB_INDEX_KEY.DIA:
      id = PROPERTY_ID.DIA
      break
  }
  return id
}

/**
 * 아이템 ID의 고유 문자열을 반환한다.
 * 추후 DB에 저장된 아이템 이름을 참조하도록 변경되어야 한다.
 *
 * @param id
 */
export function getTextByPropertyId(id: PROPERTY_ID): string {
  let text = ''

  switch (id) {
    case PROPERTY_ID.MONEY:
      text = 'G머니'
      break
    case PROPERTY_ID.HEART:
      text = '하트'
      break
    case PROPERTY_ID.POINT:
      text = '포인트'
      break
    case PROPERTY_ID.MILEAGE:
      text = '마일리지'
      break
    case PROPERTY_ID.DIA:
      text = '다이아'
      break
    case PROPERTY_ID.NEW_BET_TICKET_G:
      text = '베팅권'
      break
  }

  return text
}

export function getAccountTypeText(accountType: ACCOUNT_TYPE) {
  let text = ''
  switch (accountType) {
    case ACCOUNT_TYPE.GOOGLE:
      text = '구글'
      break
    case ACCOUNT_TYPE.NAVER:
      text = '네이버'
      break
    case ACCOUNT_TYPE.KAKAO:
      text = '카카오'
      break
    case ACCOUNT_TYPE.LOCAL:
      text = '멤버쉽'
      break
    case ACCOUNT_TYPE.APPLE:
      text = '애플'
      break
  }
  return text
}

export function makeMaxBetAlertText(betMoneyType: PROPERTY_ID, maxBetMoney: number): string {
  let betMoneyTypeText = 'G'
  if (betMoneyType === PROPERTY_ID.DIA) {
    betMoneyTypeText = ' 다이아'
  }

  return `1회 최대 ${maxBetMoney.toLocaleString()}${betMoneyTypeText}까지 베팅할 수 있습니다.`
}

export function makeLimitBettingRateText(limitBettingRate: number): string {
  let text = ''

  if (_.isNil(limitBettingRate)) {
    text = '100'
  } else if (limitBettingRate < 0) {
    text = '무제한'
  } else {
    text = limitBettingRate.toLocaleString()
  }

  return text
}

export function isViewPage(pageType: PAGE_TYPE) {
  const hash = routerStore.location.hash || window.location.hash
  const viewPath = _.startsWith(hash, '#/') ? hash.substr(2) : hash
  const viewPathSplit = viewPath.split('/')
  return viewPathSplit[0] === pageType
}

export function isViewHomePage(pageType: PAGE_WITHOUT_HEADER) {
  const hash = routerStore.location.hash || window.location.hash
  const viewPath = _.startsWith(hash, '#/') ? hash.substr(2) : hash
  const viewPathSplit = viewPath.split('/')
  return viewPathSplit[0] === pageType
}

export function maxImageFileSizeValidate(file: File) {
  const filesize = Number(((file.size / 1024) / 1024).toFixed(4))
  if (filesize > 13) {
    showToast(TOAST_TYPE.WARN, '이미지 용량은 최대 13M 까지만 가능합니다.')
    return false
  }

  return true
}

export function getV2ServerTypeToHomepageServerType() {
  let serverType = HOMEPAGE_SERVER_TYPE.TEST
  if (isProdEnv()) {
    serverType = HOMEPAGE_SERVER_TYPE.PROD
  } else if (isQaEnv()) {
    serverType = HOMEPAGE_SERVER_TYPE.QA
  }
  return serverType
}

export function makeAuthenticationUrlByType(type: AUTHENTICATION_TYPE) {
  let url = process.env.PHONE_AUTHENTICATION_URL
  let pagePrefix = 'phone'

  if (type === AUTHENTICATION_TYPE.I_PIN) {
    url = process.env.IPIN_AUTHENTICATION_URL
    pagePrefix = 'ipin'
  }

  url += `&redurl=${process.env.BACKEND_BASE_URL}:${process.env.BACKEND_PORT}/${process.env.BACKEND_PREFIX}/join/${pagePrefix}AuthenticationResult`
  url += `&serverType=${getV2ServerTypeToHomepageServerType()}`
  url += '&sid='
  url += '&reauth=false'
  if(type === AUTHENTICATION_TYPE.PHONE) {
     url += `&notiurl=${process.env.BACKEND_BASE_URL}:${process.env.BACKEND_PORT}/${process.env.BACKEND_PREFIX}/join/phoneAuthenticationNoti`
    // url += `&notiurl=https://krystle-unreclining-slopingly.ngrok-free.dev/${process.env.BACKEND_PREFIX}/join/phoneAuthenticationNoti`
    // url += `&notiurl=http://host.docker.internal:11000/api/join/phoneAuthenticationNoti`
  }
  return url
}

function loadOrReloadForIOS(pathWithSlash: PAGE_TYPE | string, allowDuplicateHistory: boolean = true): void {
  const targetHash = `#${pathWithSlash}`
  const currentHash = decodeURIComponent(window.location.hash)

  if (!allowDuplicateHistory) {
    if (currentHash === targetHash) {
      routerStore.replace('/empty')
      setTimeout(() => {
        routerStore.replace(`${pathWithSlash}`)
      }, 10)
      return
    }
  }

  const link = document.createElement('a')
  link.href = targetHash
  link.style.display = 'none'
  document.body.appendChild(link)

  link.click() // 강제 클릭 발생

  // 메모리 누수 방지 차원에서 비동기로 제거
  setTimeout(() => {
    document.body.removeChild(link)
  }, 10)
}

export function loadOrReload(pageType: PAGE_TYPE | string, allowDuplicateHistory: boolean = true): void {
  const pathWithSlash = pageType.startsWith('/') ? pageType : `/${pageType}`
  if (isIOS) {
    loadOrReloadForIOS(pathWithSlash, allowDuplicateHistory)
    return
  }

  if (!allowDuplicateHistory) {
    const path = routerStore.location.pathname
    if (_.startsWith(path, pathWithSlash)) {
      // 동일한 페이지를 다시 로드 시 history에 중복되어 저장되는 현상 존재
      // 같은 pageType 일 경우, 현재 페이지를 empty로 대체 후 다시 로드
      routerStore.replace('/empty')
    } else {
      // 동일한 페이지를 push 하면 재로드 하지 않는다. router는 reload 를 지원하지 않음.
      // empty 페이지 호출 후 replace 를 사용해 history 를 유지하면서 reload 한다.
      routerStore.push('/empty')
    }

    setTimeout(() => {
      routerStore.replace(pathWithSlash)
    }, 10)
    return
  }

  routerStore.push(pathWithSlash)
}

export function getInitializeClubId(wantClubId?: string) {
  let clubId = ''

  // 전용 클럽
  clubId = authStore?.userInfo?.clubId
  if (authStore?.userInfo?.isSpecificClubMember) {
    return clubId
  }

  // 명시적으로 원하는 클럽
  clubId = wantClubId
  if (!_.isEmpty(_.trim(clubId))) {
    return clubId
  }

  // 현재 인게임에서 접속한 상태인 클럽
  clubId = matchupStore?.club?.id
  if (!_.isEmpty(_.trim(clubId))) {
    return clubId
  }

  // 사용자와 관련된 클럽 (전용클럽, 베팅클럽, 방문 클럽, 가입된 클럽)
  clubId = authStore?.userInfo?.clubId
  if (!_.isEmpty(_.trim(clubId))) {
    return clubId
  }

  // 기기의 마지막 접속 클럽
  clubId = StorageUtils.getLastClubId()
  if (!_.isEmpty(_.trim(clubId))) {
    return clubId
  }

  // 글로벌 값의 대표 클럽
  clubId = globalStore.findGlobalSetting(GlobalStoreKey.REPRESENTATIVE_CLUB)?.value
  if (!_.isEmpty(_.trim(clubId))) {
    return clubId
  }

  // 환경변수에 설정된 기본 클럽
  clubId = process.env.DEFAULT_CLUB_ID

  return clubId
}

/**
 * 다이아 홍보 이미지를 마지막으로 표시된 일자 정보의 로컬 저장소 키를 반환한다.
 *
 * @param type
 */
export function getLocalStorageKeyByDiaAdvertisementImageLastShowedAt(
  type: DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE,
) {
  let key
  switch (type) {
    case DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE.GIFT_POPUP:
      key = LOCALSTORAGE_KEYS.DIA_ADVERTISE_IMAGE_LAST_SHOWED_AT_BY_GIFT_BOX
      break
    case DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE.BET_SLIP_BOTTOM:
      key = LOCALSTORAGE_KEYS.DIA_ADVERTISE_IMAGE_LAST_SHOWED_AT_BY_BETSLIP_BOTTOM
      break
  }
  return key
}

/**
 * 다이아 홍보 이미지 표시 여부를 반환한다.
 *
 * @param type
 */
export function isShowDiaAdvertisementImage(type: DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE) {
  const enabled = globalStore.getDiaAdvertisementImageDisplayEnabled()
  if (!enabled) {
    return false
  }

  const { hasDiaBettingHistory } = userStore
  if (hasDiaBettingHistory) {
    return false
  }

  const lastShowedAt = localStorage.getItem(getLocalStorageKeyByDiaAdvertisementImageLastShowedAt(type))
  if (_.isEmpty(lastShowedAt)) {
    return true
  }

  const startOfToday = dayjs().startOf('day')
  const startOfLastShowed = dayjs(lastShowedAt).startOf('day')
  const cycleDay = globalStore.getDiaAdvertisementImageDisplayCycleDay()

  return startOfToday.diff(startOfLastShowed, 'day') >= Number(cycleDay)
}

/**
 * 카지노 홍보 팝업 표시 여부를 반환한다.
 */
export function isShowCasinoAdvertisementPopup(): boolean {
  if (matchupStore.currentGameKey === GAME_TYPE.LIVE) {
    return false
  }

  const enabled = globalStore.getCasinoAdvertisementPopupDisplayEnabled()
  if (!enabled) {
    return false
  }

  const { preMatchBetSuccessCountAfterBetSlipOpened } = matchupStore
  if (isMobile && preMatchBetSuccessCountAfterBetSlipOpened < 1) {
    return false
  }

  const { hasCasinoLoginHistory } = userStore
  if (hasCasinoLoginHistory) {
    return false
  }

  const lastShowedAt = StorageUtils.getCasinoAdvertisementLastShowedAt()
  if (_.isEmpty(lastShowedAt)) {
    return true
  }

  const startOfToday = dayjs().startOf('day')
  const startOfLastShowed = dayjs(lastShowedAt).startOf('day')
  const cycleDay = globalStore.getCasinoAdvertisementPopupDisplayCycleDay()

  return startOfToday.diff(startOfLastShowed, 'day') >= Number(cycleDay)
}

/**
 * 카지노 홍보 팝업을 표시한다
 */
export function showCasinoAdvertisementPopup() {
  if (!isShowCasinoAdvertisementPopup()) {
    return
  }

  globalStore.setVisibleCasinoOpenPopup(true)
}

/**
 * 다이아 홍보 이미지 이미지의 마지막 표시 일자를 갱신한다.
 *
 * @param type
 */
export function updateDiaAdvertisementImageLastShowedAt(type: DIA_ADVERTISEMENT_IMAGE_DISPLAYABLE_VIEW_TYPE) {
  const enabled = globalStore.getDiaAdvertisementImageDisplayEnabled()
  if (!enabled) {
    return
  }

  localStorage.setItem(getLocalStorageKeyByDiaAdvertisementImageLastShowedAt(type), _.toString(new Date()))
}

/**
 * 카지노 홍보 팝업 마지막 표시 일자를 갱신한다.
 *
 */
export function updateCasinoAdvertisementImageLastShowedAt() {
  const enabled = globalStore.getCasinoAdvertisementPopupDisplayEnabled()
  if (!enabled) {
    return
  }

  StorageUtils.saveCasinoAdvertisementLastShowedAt()
}

/**
 * 테스트를 위한 외부 계정 여부를 반환한다
 *
 * @param userId
 */
export function isAccountForTesting(userId: string): boolean {
  const testUserIds = globalStore.getUserIdsForTest()
  const { android } = window

  if (android) {
    return _.includes(testUserIds, userId)
  }

  return false
}

/**
 * 승부사 온라인 URL을 생성한다.
 */
export function makeAdventurerUrl(): string {
  const port = process.env.FRONTEND_PORT ? `:${process.env.FRONTEND_PORT}` : ''
  const globalValue = globalStore.findGlobalSetting(GlobalStoreKey.DEFAULT_GUEST_CLUB)?.value
  const guestClubId = globalValue ?? process.env.DEFAULT_GUEST_CLUB_ID
  return `${process.env.FRONTEND_BASE_URL}${port}/#/matchup/cross/${guestClubId}`
}

/**
 * 승부사 온라인 홈페이지 URL을 생성한다.
 */
export function makeAdventurerHomePageUrl(): string {
  return `${process.env.HOMEPAGE_PC}/#/mb_join`
}

/**
 * 문자열을 클립보드에 복사한다.
 *
 * Deprecated: https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
 * https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
 * https://developer.mozilla.org/en-US/docs/Web/API/Clipboard
 *
 * Document.execCommand 는 더이상 사용되지 않지만,
 * Clipboard API가 동작하지 않는 환경을 위해 관련 코드를 추가함
 *
 * @param text
 */
export async function textCopyToClipboard(text: string): Promise<void> {
  const isAccessible = await isAccessibleNavigatorClipboardWrite()
  if (isAccessible) {
    await navigator.clipboard.writeText(text)
  } else {
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', '')
    el.style.position = 'absolute'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
}

/**
 * navigator.clipboard 접근 권한을 확인한다.
 *
 */
export async function isAccessibleNavigatorClipboardWrite(): Promise<boolean> {
  try {
    if (!_.isFunction(navigator?.clipboard?.writeText)) {
      return false
    }

    const result = await navigator?.permissions?.query({ name: 'clipboard-write' as PermissionName })
    return result?.state === 'granted' || result?.state === 'prompt'
  } catch (err) {
    console.error(err)
  }

  return false
}

export function isLiveScoreRecommender(recommender: string) {
  const value = globalStore.findGlobalSetting(GlobalStoreKey.LIVE_SCORE_RECOMMEND_CODES)?.value ?? '[]'
  const codes = JSON.parse(value)
  return _.includes<string>(codes, recommender)
}

export function isLsportsEnv() {
  return matchupStore.getMarketProvider() !== MARKET_PROVIDER.BET_RADAR
}

export function isTestClub(clubId: string): boolean {
  const globalSetting = globalStore.findGlobalSetting(GlobalStoreKey.CLUB_ID_LIST_FOR_TESTING)
  if (_.isNil(globalSetting)) {
    return false
  }
  const clubIds = JSON.parse(globalSetting?.value) || []
  return _.includes(clubIds, clubId)
}

export function formatCreatedAtToElapsedTime(params: {
  createdAt: Date,
  modifiedAt?: Date,
  nowAt?: Date,
  overDateText?: string
}): string {
  const { createdAt, modifiedAt, nowAt = new Date(), overDateText } = params
  if (_.isNil(createdAt)) {
    return ''
  }

  const now = dayjs(nowAt)
  const minuteLimitAt = now.add(-60, 'minute') // 60분 이내
  const hourLimitAt = now.add(-24, 'hour')  // 24시간 이내
  const dayLimitAt = now.add(-7, 'day')     // 7일 이내
  const weekLimitAt = now.add(-5, 'week')   // 5주 이내

  let baseDate = dayjs(createdAt)
  if (modifiedAt) {
    baseDate = dayjs(modifiedAt)
  }

  let prefix
  let suffix
  if (baseDate.isAfter(minuteLimitAt)) {
    prefix = now.diff(baseDate, 'minute')
    suffix = '분 전'
  } else if (baseDate.isAfter(hourLimitAt)) {
    prefix = now.diff(baseDate, 'hour')
    suffix = '시간 전'
  } else if (baseDate.isAfter(dayLimitAt)) {
    prefix = now.diff(baseDate, 'day')
    suffix = '일 전'
  } else if (baseDate.isAfter(weekLimitAt)) {
    prefix = now.diff(baseDate, 'week')
    suffix = '주 전'
  } else if (!_.isNil(overDateText)) {
    prefix = ''
    suffix = overDateText
  } else {
    prefix = baseDate.format('YYYY/MM/DD')
    suffix = ''
  }

  let modifiedDate
  if (modifiedAt) {
    modifiedDate = dayjs(modifiedAt)
  }
  const createdDate = dayjs(createdAt)
  if (modifiedAt && !modifiedDate.isSame(createdDate)) {
    suffix += '(수정됨)'
  }
  return prefix + suffix
}

export function isMatchupPage(pathname?: string): boolean {
  if (!_.isNil(pathname)) {
    return pathname.startsWith('/matchup/')
  }
  return window.location.href.includes('#/matchup/')
}

/**
 * 승부사 안드로이드 앱 웹뷰인지 확인한다.
 */
export function isAdventurerAndroidApp(): boolean {
  return Boolean(window.android && window.navigator.userAgent.includes('ADV_1'))
}

export function isHomePage(): boolean {
  return window.location.href.includes('#/home')
}

/**
 * 경기 시간을 기준으로 시작된 경기 인지 판단 한다.
 *
 * 시작된 경기 시간 : false
 *
 * 시작전 경기 시간 : true
 *
 * @param dateOfMatch
 */
export function isNotStartedMatch(dateOfMatch: string | Date): boolean {
  return dayjs().isBefore(dayjs(dateOfMatch))
}

export function isStartedMatch(dateOfMatch: string | Date): boolean {
  return dayjs(dateOfMatch).isBefore(dayjs())
}

/**
 * 앱의 메인 스크롤 위치를 이동시킨다.
 * @param position
 */
export function appScrollTo(position: number): void {
  document.getElementsByTagName('html')[0].scrollTop = position
}

export function getAppScrollTop(): number {
  return document.getElementsByTagName('html')[0].scrollTop || 0
}

/**
 * <ListView> 컴포넌트의 스크롤을 가장 상단으로 이동 시켜 준다.
 *
 * @param {string} keyClassName <ListView> 의 속성중 className 에 입력된 다른 컴포넌트와 구분되는 키 클래스 이름
 */
export function listViewScrollMoveTop(keyClassName: string): void {
  const viewElem = document.querySelector(`.${keyClassName}.am-list-view-scrollview`)
  if (viewElem) {
    viewElem.scrollTop = 0
  }
}

export function matchupDetailScrollToTop(): void {
  setTimeout(() => {
    document.getElementById('matchDetailWrap').scrollTop = 0
  }, 0)
}

export function isExtraOddsItemSupportBetMoneyType(propertyId: PROPERTY_ID): boolean {
  const value = globalStore.findGlobalSetting(GlobalStoreKey.GOD_BLESS_ITEM_SUPPORT_BET_MONEY_TYPES)?.value
    ?? `[${PROPERTY_ID.MONEY}, ${PROPERTY_ID.NEW_BET_TICKET_G}]`
  const allowBetMoneyType: number[] = JSON.parse(value)
  return _.includes(allowBetMoneyType, propertyId)
}

export function isAvailableGotBlessItemFoldCondition(cartItems: CartItem[]): boolean {
  const { minimumOutcomeRate, minimumOutcomeRateFoldCount } = globalStore.getGodBlessItemAvailableFoldCondition()
  const found = _.filter(cartItems, cartItem => _.toNumber(cartItem.rate) > minimumOutcomeRate)
  return _.size(found) >= minimumOutcomeRateFoldCount
}

export function makeGodBlessItemAvailableFoldConditionInvalidDialogText(): string[] {
  const { minimumOutcomeRate, minimumOutcomeRateFoldCount } = globalStore.getGodBlessItemAvailableFoldCondition()
  return [
    `${minimumOutcomeRate}배당 이상의 경기가`,
    `${minimumOutcomeRateFoldCount}폴드 이상인 경우 사용할 수 있습니다.`,
  ]
}

export function makeAttachmentDetailObjToBettingRecordTrack(attachmentDetailObj: any): BettingRecordTrack {
  const bettingRecordTrack = { bet: attachmentDetailObj } as BettingRecordTrack
  if (_.isEmpty(attachmentDetailObj?.sportIds)) {
    return bettingRecordTrack
  }

  const folds = []
  _.forEach(attachmentDetailObj?.sportIds, sportId => {
    folds.push({ sportId })
  })
  return _.extend(bettingRecordTrack, { folds })
}

export function replaceSelectionNameByMarketAndSpecialBetValue(
  selectionName: string, marketName: string, specialBetValue: string,
): string {
  let name = selectionName.replace(specialBetValue, '')
  if (name === '무') {
    name = '무승부'
  } else if (marketName === '승무패' || marketName === '승패' || marketName === '승패 (연장포함)') {
    name = `${selectionName} 승`
  }
  return `${name}`
}

/**
 * 1000 단위 이상 숫자에 K
 * 1,000,000 단위 이상 숫자에 M 을 붙여 준다.
 *
 * 예) 1,100 > 1.1K, 3,200,000 > 3.2M
 * @param value
 */
export function toShortNumber(value: string | number): string {
  if (_.isNil(value)) {
    return '0'
  }
  const source = Number(value)

  if (source < 1000) {
    return value.toString()
  }

  let result: string
  if (source >= 1_000 && 999_999 >= source) {
    // 1e2 = 100
    const cValue = Math.floor(source / 1e2) / 10
    result = `${cValue}K`
  } else if (source >= 1_000_000 && 999_999_999 >= source) {
    // 1e5 = 100_000
    const cValue = Math.floor(source / 1e5) / 10
    result = `${cValue}M`
  }

  return result
}

export function isConsideredOngoingMatchStatus(matchStatus: MATCH_STATUS, dateOfMatch: Date): boolean {
  const now = new Date()

  return isOngoingMatchStatus(matchStatus)
    || (
      matchStatus === MATCH_STATUS.NotStarted
      && (
        dayjs(dateOfMatch).isBefore(now)
        || dayjs(dateOfMatch).isSame(now)
      )
    )
}

export function isOngoingMatchStatus(matchStatus: MATCH_STATUS): boolean {
  if (matchStatus == null) {
    return false
  }

  const status = Number(matchStatus)
  if (!Number.isFinite(status)) {
    return false
  }

  return (
    status > MATCH_STATUS.NotStarted &&
    status !== MATCH_STATUS.Suspended &&
    status < MATCH_STATUS.Abandoned
  ) || (
    status > MATCH_STATUS.Ended &&
    status !== MATCH_STATUS.AET &&
    status !== MATCH_STATUS.AOT &&
    status !== MATCH_STATUS.AP
  )
}

export function isClosedMatchStatus(matchStatus: MATCH_STATUS): boolean {
  if (_.isNil(matchStatus)) {
    return false
  }

  const status = Number(matchStatus)
  if (!Number.isFinite(status)) {
    return false
  }

  return status === MATCH_STATUS.AOT || status === MATCH_STATUS.AP || status === MATCH_STATUS.AET
    || (status >= MATCH_STATUS.Abandoned && status <= MATCH_STATUS.Ended)
}

export function isCanceledMatchStatus(matchStatus: MATCH_STATUS): boolean {
  if (_.isNil(matchStatus)) {
    return false
  }

  const status = Number(matchStatus)
  if (!Number.isFinite(status)) {
    return false
  }

  return status === MATCH_STATUS.Cancelled || status === MATCH_STATUS.Suspended
    || status === MATCH_STATUS.Postponed
}

export function isNotStartedMatchStatus(matchStatus: MATCH_STATUS): boolean {
  if (matchStatus == null) {
    return false
  }

  const status = Number(matchStatus)
  if (!Number.isFinite(status)) {
    return false
  }

  return status === MATCH_STATUS.NotStarted
}

export function isPeriodScoreMatch(sportId: SPORT_ID): boolean {
  return sportId === SPORT_ID.VOLLEYBALL
    || sportId === SPORT_ID.TENNIS
    || sportId === SPORT_ID.TABLE_TENNIS
}

export function convertMatchCountText(count: string | number): string {
  if (_.isNil(count)) {
    return ''
  }

  if (_.toNumber(count) < 1000) {
    return _.toString(count)
  }

  return '999+'
}

export function isMajorSportsType(sportType: string) {
  switch (sportType) {
    case SPORTS_TYPE.TODAY:
    case SPORTS_TYPE.SOCCER:
    case SPORTS_TYPE.BASKETBALL:
    case SPORTS_TYPE.BASEBALL:
    case SPORTS_TYPE.VOLLEYBALL:
    case SPORTS_TYPE.ICE_HOCKEY:
      return true
  }
  return false
}

/**
 * 팀명이 길어질 경우를 대비하여 특정 조건에 만족하면 줄바꿈 처리를 해준다.
 * 1. 팀명이 한글인 경우 or 팀명이 한글 +영문인 경우
 * ㄱ. 총 글자수가 8자를 초과하는 팀명
 * ㄴ. 팀명 내 띄어쓰기가 존재
 *
 * 위 두가지를 만족하는 팀명은
 * 첫번째 띄어쓰기에서 개행
 *
 * Ex)팀명: 피츠버그 파이어리츠
 *
 * 경기결과 :
 * 피츠버그
 * 파이어리츠
 *
 * 2. 팀명이 영문인 경우
 * ㄱ. 총 글자수가 13자를 초과하는 팀명
 * ㄴ. 팀명 내 띄어쓰기가 존재
 *
 * 위 두가지를 만족하는 팀명은
 * 첫번째 띄어쓰기에서 개행
 *
 * Ex)팀명 : Santo AndreIntelli SP
 *
 * 경기결과 :
 * Santo
 * AndreIntelli
 * SP
 *
 * 억지로 개행시키는것 첫번쨰 띄어쓰기만 적용되면되고
 * 이후는 지금처럼 자동개행되면 됩니다
 * @param teamName
 */
export function addNewLineToTeamName(teamName: string): string {
  const strType = checkStringType(teamName)
  const teamNameLength = _.size(teamName)
  const hasWhitespace = _.indexOf(teamName, ' ') > -1
  if (strType === 'en') {
    if (teamNameLength > 13 && hasWhitespace) {
      return teamName.replace(' ', '\n')
    }
  } else if ((strType === 'ko' || strType === 'ko_en')) {
    if (teamNameLength > 8 && hasWhitespace) {
      return teamName.replace(' ', '\n')
    }
  }
  return teamName
}

/**
 * 전달받은 문자열이 한글인지, 영문인지 체크 하는 함수
 * @param str
 * @returns ko: 한글만, en: 영문만, ko_en: 한글+영문
 */
export function checkStringType(str: string): 'ko' | 'en' | 'ko_en' {
  const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(str)
  const hasEnglish = /[a-zA-Z]/.test(str)

  if (hasKorean && hasEnglish) {
    return 'ko_en'
  } else if (hasKorean) {
    return 'ko'
  } else if (hasEnglish) {
    return 'en'
  }
  return null
}

export function populateSimpleMatchTimelineGroupToSimpleMatchTimeline(groups: SimpleMatchTimelineGroup[]): void {
  _.forEach(groups, group => {
    const sportId = group.sportId
    const sportName = group.sportName
    const emblemSport = group.emblemSport
    const tournamentId = group.tournamentId
    const tournamentName = group.tournamentName
    const emblemTournament = group.emblemTournament
    const categoryId = group.categoryId
    const categoryName = group.categoryName
    const emblemCategory = group.emblemCategory
    _.forEach(group.simpleMatchTimelineList, timeline => {
      timeline.sportId = sportId
      timeline.sportName = sportName
      timeline.emblemSport = emblemSport
      timeline.tournamentId = tournamentId
      timeline.tournamentName = tournamentName
      timeline.emblemTournament = emblemTournament
      timeline.categoryId = categoryId
      timeline.categoryName = categoryName
      timeline.emblemCategory = emblemCategory
    })
  })
}

export function toUserActivityTypeDesc(userActivityType: USER_ACTIVITY_TYPE): string {
  let userActivityDesc = ''

  switch (userActivityType) {

    case USER_ACTIVITY_TYPE.COMPANY_PICK_COMMENT:
    case USER_ACTIVITY_TYPE.USER_PICK_COMMENT:
      userActivityDesc = '댓글 및 답글 작성'
      break
    case USER_ACTIVITY_TYPE.COMPANY_PICK_LIKED:
    case USER_ACTIVITY_TYPE.USER_PICK_LIKED:
      userActivityDesc = '분석글 좋아요'
      break
    case USER_ACTIVITY_TYPE.USER_PICK_WRITTEN:
      userActivityDesc = '분석글 작성'
      break
    case USER_ACTIVITY_TYPE.TODAY_USER_PICK:
      userActivityDesc = '오늘의 승부사 선정'
      break
    case USER_ACTIVITY_TYPE.USER_PICK_RECEIVED_COMMENT:
      userActivityDesc = '내 게시물 댓글'
      break
    case USER_ACTIVITY_TYPE.USER_PICK_RECEIVED_LIKE:
      userActivityDesc = '내 게시물 좋아요'
      break
    case USER_ACTIVITY_TYPE.BET_HISTORY_SHARED_TO_CHAT:
      userActivityDesc = '채팅 베팅내역 공유'
      break
    case USER_ACTIVITY_TYPE.CHEER_FOR_TEAM:
      userActivityDesc = '응원글 작성'
      break
  }

  return userActivityDesc
}

export function getCommunicationBlockTypeText(
  communicationblocktype: COMMUNICATION_BLOCK_TYPE
): string {
  let blockTypeText = ''

  switch (communicationblocktype) {
    case COMMUNICATION_BLOCK_TYPE.USER_PICK:
      blockTypeText = '분석글 차단'
      break
    case COMMUNICATION_BLOCK_TYPE.PICK_COMMENT:
      blockTypeText = '댓글 및 답글 차단'
      break
    case COMMUNICATION_BLOCK_TYPE.MESSAGE:
      blockTypeText = '메시지 차단'
      break
    case COMMUNICATION_BLOCK_TYPE.CHAT_AND_SPORT_TALK:
      blockTypeText = '채팅 및 스포츠톡 차단'
      break
  }

  return blockTypeText
}

export function calculateCircumference(radius: number): number {
  return 2 * Math.PI * radius
}

export function makeRemainingActivityScore(ignoreMinusRemaining: boolean = false): number {
  const beforeScore = globalStore.userActivityInfo?.beforeActivityRewardConditionScore ?? 0
  const nextScore = globalStore.userActivityInfo?.nextActivityRewardConditionScore ?? 0
  const currentScore = globalStore.userActivityInfo?.monthlyActivityScore ?? 0

  if (ignoreMinusRemaining && currentScore < beforeScore) { // 이전 보상 수령한 기준보다 현재 스코어가 작으면 게이지는 0으로 표시
    return 0
  }

  // 다음 보상 활동 점수 기준 값이 없고, 내 활동 점수가 0보다 크면 모든 보상을 다 받은 상태
  if (nextScore === 0 && currentScore > 0) {
    return 0
  }

  return nextScore - currentScore
}

export function makeActivityGageConfig(radius: number): { dashOffset: number, circumference: number } {
  const circumference = calculateCircumference(radius)
  const remainingScore = makeRemainingActivityScore()

  const activityRewardAvailable = globalStore.userActivityInfo?.activityRewardAvailable
  const beforeScore = globalStore.userActivityInfo?.beforeActivityRewardConditionScore ?? 0
  const nextScore = globalStore.userActivityInfo?.nextActivityRewardConditionScore ?? 0
  const currentScore = globalStore.userActivityInfo?.monthlyActivityScore ?? 0

  // 활동 점수 보상을 수령한 이후 현재 활동 점수가 수령한 활동 점수 보상보다 낮을 경우 활동 점수 게이지는 0으로 표시해야 한다.
  if (currentScore < beforeScore) {
    return { dashOffset: circumference, circumference }
  }

  if (remainingScore === 0 && !activityRewardAvailable) { // 모든 보상을 다 받았을 때
    return { dashOffset: circumference, circumference }
  }

  if (activityRewardAvailable) {
    return { dashOffset: 0, circumference }
  }

  const rangeScore = nextScore - beforeScore
  const gage = remainingScore / rangeScore * 100
  const dashOffset = (gage / 100) * circumference

  return { dashOffset, circumference }
}

export function isShowMatchRecordButton(gameType: GAME_TYPE, pageType: PAGE_TYPE, sportId: number): boolean {
  return (gameType === GAME_TYPE.LIVE
      || pageType === PAGE_TYPE.MATCHUP_RESULT
      || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)
    && _.includes(TIMELINE_POPUP_ENABLED_SPORT_IDS, sportId)
}

export function formatMatchStatusNameToCheerForTeamMatchStatusName(params: {
  matchStatus: MATCH_STATUS,
  matchStatusName: string,
  dateOfMatch: Date,
  baseDateAt: Date,
}): string {
  const { matchStatus, matchStatusName, dateOfMatch, baseDateAt } = params

  let name = matchStatusName
  if (isNotStartedMatchStatus(matchStatus)) {
    if (dayjs(baseDateAt).isAfter(dayjs(dateOfMatch))) {
      name = '시작대기'
    } else {
      name = formatCreatedAtToElapsedTime({ createdAt: baseDateAt, modifiedAt: null, nowAt: dateOfMatch })
    }
  }
  return name
}

export function isMembershipClub(grade: number): boolean {
  if (_.isNil(grade)) {
    return false
  }
  return grade !== CLUB_MEMBERSHIP_ID.NONE && grade !== CLUB_MEMBERSHIP_ID.FREE
}

export function getMatchInsightByTimeline(params: {
  timeline: string,
  homeTeamName: string,
  awayTeamName: string,
  homeTeamEmblemUrl: string,
  awayTeamEmblemUrl: string
}): {
  eventType: string,
  scoreTeamName: string,
  scoreTime: number,
  goalScorer: string,
  emblemUrl: string,
  homeTeamScore: number,
  awayTeamScore: number
} {
  const { timeline: timelineParam, homeTeamName, awayTeamName, homeTeamEmblemUrl, awayTeamEmblemUrl } = params
  const timeline = JSON.parse(timelineParam) as object
  const eventType = timeline['type']
  const homeTeamScore = timeline['homeScore']
  const awayTeamScore = timeline['awayScore']
  const scoreTeamName = timeline['team'] === 'home' ? homeTeamName : awayTeamName
  const scoreTime = timeline['matchClock']
  const goalScorer = timeline['goalScorer'] ? timeline['goalScorer'].name : ''
  const emblemUrl = timeline['team'] === 'home' ? homeTeamEmblemUrl : awayTeamEmblemUrl
  return {
    eventType,
    scoreTeamName,
    scoreTime,
    goalScorer,
    emblemUrl,
    homeTeamScore,
    awayTeamScore,
  }
}

export function makeSimpleMatchInfoTrackByDetailTrack(detailTrack: DetailMatchTrack): SimpleMatchTrack {
  if (!detailTrack) {
    return null
  }
  const {
    sportId,
    categoryId,
    categoryName,
    tournamentId,
    tournamentName,
    tournamentEmblem,
    gameKey,
  } = detailTrack

  const track = detailTrack?.track || ({} as MatchTrack)

  const {
    matchId,
    dateOfMatch,
    statusCode,
    matchStatusName,
    homeTeamName,
    awayTeamName,
    homeTeamEmblem,
    awayTeamEmblem,
    homeScore,
    awayScore,
    periodScores,
    feedSupplier,
  } = track

  return {
    matchId,
    statusCode,
    matchStatusName,
    liveOdds: gameKey === GAME_TYPE.LIVE,
    dateOfMatch,
    sportId,
    categoryId,
    categoryName,
    tournamentId,
    tournamentName,
    tournamentEmblem,
    homeTeamName,
    homeTeamEmblem,
    awayTeamName,
    awayTeamEmblem,
    homeScore,
    awayScore,
    periodScores,
    feedSupplier,
  }
}

export function isVisualizationSupported(sportId: number, categoryId: number, tournamentId: number) {
  let visualSupport = false
  switch (sportId) {
    case SPORT_ID.SOCCER:
    case SPORT_ID.BASKETBALL:
    case SPORT_ID.ICE_HOCKEY:
    case SPORT_ID.TABLE_TENNIS:
      visualSupport = true
      break
    case SPORT_ID.BASEBALL:
      if (categoryId === 16 && tournamentId === 109) {
        visualSupport = true
      }
      break
  }
  if (isLsportsEnv()) {
    visualSupport = true
  }

  return visualSupport
}

export function lockHtmlOverscroll() {
  const html = document.documentElement
  html.style.setProperty('overscroll-behavior', 'none')
}

export function unlockHtmlOverscroll() {
  const html = document.documentElement
  html.style.removeProperty('overscroll-behavior')
}
